import Phaser from 'phaser';

export interface BackdropOptions {
  // Dim/fill colour of the full-screen rectangle.
  color: number;
  // Rectangle alpha (1 = fully opaque). Defaults to 1.
  alpha?: number;
  // Optional texture key drawn behind the dim (e.g. a faint title image).
  image?: string;
  // Alpha for that image. Defaults to 0.06.
  imageAlpha?: number;
  // Depth of the dim layer (image sits one below). Defaults to -9.
  depth?: number;
}

// Adds a full-screen backdrop (a dim rectangle, optionally over a cover image)
// that stays matched to the viewport across orientation changes / resizes.
//
// Phaser game objects keep their create-time size, so a scene that drew its
// backdrop once at create() would, after a phone rotates, leave the old
// (portrait-width) rectangle only covering part of a landscape screen — a
// visible vertical seam. This keeps it re-laid on every resize and tears the
// listener down on scene shutdown.
export function mountBackdrop(
  scene: Phaser.Scene,
  opts: BackdropOptions,
): void {
  const depth = opts.depth ?? -9;

  let img: Phaser.GameObjects.Image | null = null;
  if (opts.image && scene.textures.exists(opts.image)) {
    img = scene.add.image(0, 0, opts.image);
    img.setAlpha(opts.imageAlpha ?? 0.06);
    img.setDepth(depth - 1);
  }

  const dim = scene.add.rectangle(
    0,
    0,
    scene.scale.width,
    scene.scale.height,
    opts.color,
    opts.alpha ?? 1,
  );
  dim.setDepth(depth);

  const layout = (): void => {
    const w = scene.scale.width;
    const h = scene.scale.height;
    dim.setPosition(w / 2, h / 2);
    dim.setSize(w, h);
    if (img) {
      img.setPosition(w / 2, h / 2);
      const s = Math.max(w / img.width, h / img.height);
      img.setScale(s);
    }
  };

  layout();
  scene.scale.on('resize', layout);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.scale.off('resize', layout);
  });
}
